import { useState, useEffect, useCallback, useMemo } from 'react';
import i18n from '../i18n';
import { groupMovements, fetchCategories, searchMovements } from '../api/endpointsBackend';

/**
 * Custom hook for fetching dashboard data using the /movements/group endpoint
 * Makes multiple API calls to get different groupings needed for the dashboard
 *
 * @param {Object} dateRange - Date range object with start_date and end_date
 * @param {Array} filters - Optional array of filter objects
 * @param {Object} options - `{ includeReview, enabled }`: `includeReview`
 *   false skips the needs-review search (the phone's Home counts review
 *   movements on its own); `enabled` false holds the fetch (the phone's Home
 *   waits until it knows which currency to ask for)
 */
export function useDashboardData(dateRange, filters = [], filterGroups = [], groupsOperator = 'and', { includeReview = true, enabled = true } = {}) {
  const [data, setData] = useState({
    summaryByType: [],      // Income/Expenses totals by currency
    categoryBreakdown: [],  // Expenses by category
    categories: [],         // Full category list (for icons/colors)
    incompleteMovements: []  // Movements missing account/category
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Serialize filters for stable dependency comparison
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  const groupsKey = useMemo(() => JSON.stringify(filterGroups), [filterGroups]);

  // Serialize dateRange for stable dependency comparison
  const dateRangeKey = useMemo(() => JSON.stringify(dateRange), [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Make parallel API calls for different groupings
      const [summaryResponse, categoryResponse, categoriesResponse, incompleteResponse] = await Promise.all([
        // 1. Summary by type (Income/Expenses totals)
        groupMovements(
          filters,
          ['currency', 'type'],
          dateRange?.start_date || null,
          dateRange?.end_date || null,
          filterGroups,
          groupsOperator
        ),
        // 2. Category breakdown (for category chart)
        groupMovements(
          [
            ...filters,
            { entity: 'Movement', column: 'type', operator: 'in', values: ['Income', 'Expenses'] }
          ],
          ['currency', 'type', 'category_id'],
          dateRange?.start_date || null,
          dateRange?.end_date || null,
          filterGroups,
          groupsOperator
        ),
        // 3. Categories list (for icons/colors)
        fetchCategories(),
        // 4. Movements needing review (MI-282 Rev2: any active alert, not just
        //    incomplete — incomplete + no-statement + direction/account/currency).
        //    Carries the page's filters like every other card: a review list
        //    that ignores them offers movements the rest of the dashboard has
        //    already excluded, next to totals reading zero.
        includeReview ? searchMovements(
          [
            ...filters,
            { entity: 'Movement', column: 'needs_review', operator: 'eq', values: [true] },
          ],
          null,
          dateRange?.start_date || null,
          dateRange?.end_date || null,
          50, 0,
          filterGroups,
          groupsOperator
        ).catch(() => ({ items: [] })) : { items: [] }
      ]);

      setData({
        summaryByType: summaryResponse || [],
        categoryBreakdown: categoryResponse || [],
        categories: categoriesResponse || [],
        incompleteMovements: incompleteResponse?.items || []
      });
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to fetch dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeKey, filtersKey, groupsKey, groupsOperator, includeReview]);

  useEffect(() => {
    if (enabled) fetchData();
  }, [fetchData, enabled]);

  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
}

/**
 * Build a category lookup map from categories array
 * @param {Array} categories - Full category list from API
 * @returns {Object} Map of category_id → {name, icon, color, type}
 */
export function buildCategoryMap(categories) {
  const map = {};
  (categories || []).forEach(cat => {
    map[cat.id] = {
      name: cat.name,
      icon: cat.icon || null,
      color: cat.color || null,
      type: cat.type
    };
  });
  return map;
}

/**
 * Transform grouped API data into dashboard metrics format
 * @param {Object} data - Raw grouped data from API
 * @param {Object} categoryMap - Optional map of category_id → {icon, color}
 * @returns {Object} Dashboard metrics in the expected format
 */
export function transformGroupedDataToMetrics(data, categoryMap = {}) {
  const { summaryByType, categoryBreakdown } = data;

  // Calculate expenses and income by currency
  const expenses = {};
  const income = {};

  summaryByType.forEach(item => {
    const currency = item.currency || 'COP';
    if (item.type === 'Expenses') {
      // total is negative for expenses, so we use absolute value
      expenses[currency] = Math.abs(item.total || 0);
    } else if (item.type === 'Income') {
      income[currency] = item.total || 0;
    }
  });

  // Calculate net balance
  const net = {};
  const allCurrencies = new Set([...Object.keys(expenses), ...Object.keys(income)]);
  allCurrencies.forEach(currency => {
    const exp = expenses[currency] || 0;
    const inc = income[currency] || 0;
    net[currency] = inc - exp;
  });

  // Build category breakdown as arrays (preserving category_id for icon/color lookup)
  const categoryBreakdownFormatted = { expenses: {}, income: {} };
  categoryBreakdown.forEach(item => {
    const currency = item.currency || 'COP';
    const categoryId = item.category_id;
    const categoryName = item.category_name || i18n.t('movements:fallback.uncategorized');
    const type = item.type?.toLowerCase() || 'expenses';
    const amount = Math.abs(item.total || 0);
    const catInfo = categoryMap[categoryId] || {};

    const section = type === 'income' ? 'income' : 'expenses';
    if (!categoryBreakdownFormatted[section][currency]) {
      categoryBreakdownFormatted[section][currency] = [];
    }

    // Check if this category already exists (same id can appear if data is duplicated)
    const existing = categoryBreakdownFormatted[section][currency].find(
      c => c.category_id === categoryId
    );
    const count = item.num_movements || 0;
    if (existing) {
      existing.amount += amount;
      existing.count = (existing.count || 0) + count;
    } else {
      categoryBreakdownFormatted[section][currency].push({
        category_id: categoryId,
        category: categoryName,
        amount,
        count,
        icon: catInfo.icon || null,
        color: catInfo.color || null,
      });
    }
  });

  // Sort each currency's categories by amount descending
  for (const section of ['expenses', 'income']) {
    for (const currency of Object.keys(categoryBreakdownFormatted[section])) {
      categoryBreakdownFormatted[section][currency].sort(
        (a, b) => b.amount - a.amount
      );
    }
  }

  return {
    expenses,
    income,
    net,
    categoryBreakdown: categoryBreakdownFormatted,
  };
}

/**
 * Build a single (currency-collapsed) category breakdown from the converted
 * `buckets` returned by /movements/converted-total when grouped by
 * ['category_id', 'type']. Each bucket is already converted to the display
 * currency at each movement's own date, so the totals reconcile with the summary
 * cards. Icons/colors/names come from the category map, matching the per-currency
 * view. Only Income/Expenses categories are shown (Transfers are excluded).
 *
 * @param {Array} buckets - ConvertedBucketSchema[] ({category_id, type, total, num_movements})
 * @param {Object} categoryMap - Map of category_id → {name, icon, color, type}
 * @returns {{expenses: Array, income: Array}} Flat, amount-sorted category lists
 */
export function buildConvertedCategoryBreakdown(buckets = [], categoryMap = {}) {
  const expenses = [];
  const income = [];
  (buckets || []).forEach(b => {
    if (b.type !== 'Income' && b.type !== 'Expenses') return;
    const info = categoryMap[b.category_id] || {};
    const row = {
      category_id: b.category_id,
      category: info.name || i18n.t('movements:fallback.uncategorized'),
      amount: Math.abs(b.total || 0),
      count: b.num_movements || 0,
      icon: info.icon || null,
      color: info.color || null,
    };
    (b.type === 'Income' ? income : expenses).push(row);
  });
  expenses.sort((a, b) => b.amount - a.amount);
  income.sort((a, b) => b.amount - a.amount);
  return { expenses, income };
}
